import { createFileRoute } from "@tanstack/react-router";
import { DriverLandingPage, type DriverLandingContent } from "@/components/DriverLandingPage";
import { seoHead, localBusinessSchema, complianceToBusinessOpts, serviceSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";
import { getPublicConfig } from "@/lib/site-content.functions";

const CONTENT: DriverLandingContent = {
  eyebrow: "Diesel DPF, EGR & Emissions",
  h1: "Diesel DPF, EGR & Emissions Repair, East London",
  intro:
    "Diesel warning light on? DPF blocked? EGR fault code stuck? Woodseer Autos in Poplar, E14 diagnoses and repairs diesel particulate filter (DPF), EGR valve, AdBlue and emissions issues for East London drivers — including high-mileage Uber, Bolt and delivery cars.",
  trustChips: [
    "Diesel diagnostics on all major makes",
    "DPF regen, clean & replacement options",
    "EGR cleaning & replacement",
    "ULEZ-compliance friendly fixes",
    "Honest written quote first",
  ],
  audienceTitle: "Who this page is for",
  audience: [
    "Diesel car drivers with engine management light",
    "Uber / Bolt drivers with DPF warnings",
    "Delivery vans (Transit, Vivaro, Sprinter)",
    "High-mileage commuters",
    "Drivers facing MOT emissions fail",
    "ULEZ-compliant car owners protecting their car",
  ],
  helpTitle: "What we cover",
  helpItems: [
    "DPF diagnostics and forced regeneration",
    "DPF deep clean and re-installation",
    "DPF replacement (OE and aftermarket)",
    "EGR valve cleaning and replacement",
    "AdBlue / SCR fault diagnostics",
    "Glow plug and injector diagnosis",
    "Turbo health checks and actuator faults",
    "Emissions advice ahead of MOT",
  ],
  issuesTitle: "Common diesel faults we see",
  issues: [
    "DPF light on after lots of short trips",
    "Limp mode under acceleration",
    "EGR valve stuck open or closed",
    "AdBlue / SCR warning with countdown",
    "Smoke at idle or under load",
    "MOT emissions fail on opacity test",
    "Loss of power and poor MPG",
    "Engine management light won't clear",
  ],
  valueTitle: "Repair, clean or replace? An honest answer",
  valueBody:
    "DPF problems are rarely 'just' the filter. We diagnose the root cause first — short journeys, faulty sensors, EGR or turbo issues — so you don't pay for a £1,000 part that fails again. Where a regen or chemical clean will solve it, that's what we recommend. Removal isn't legal on a road car and will fail your MOT, so we don't offer it.",
  beforeTitle: "Before you arrive",
  beforeItems: [
    "Note when the warning light first appeared",
    "Note any countdown messages (AdBlue / Limp mode)",
    "Roughly how often you do motorway / long-run miles",
    "Last service date and stamp",
    "Any recent dashboard codes you've seen",
    "Bring the V5C and locking wheel nut key",
  ],
  faqs: [
    { q: "Can you fix my DPF without replacing it?", a: "Often yes — a forced regeneration or chemical clean can clear a partly blocked filter. Replacement is only needed when the substrate is damaged. We diagnose first, quote both options, and tell you which makes sense." },
    { q: "Will you remove or 'delete' the DPF?", a: "No — DPF removal is illegal on a road car and an automatic MOT fail. We don't offer it. Where the filter is beyond cleaning we'll quote a replacement and explain how to avoid the same problem again." },
    { q: "How much is an EGR clean vs replacement?", a: "EGR cleans start lower than full replacement on most cars. We'll inspect, advise honestly whether cleaning will hold up, and quote both options before any work." },
    { q: "Why does my diesel keep blocking the DPF?", a: "Usually short journeys (under 20 minutes) that don't let the filter heat up. We'll advise on driving patterns, run a forced regen, and check sensors before you spend on parts." },
    { q: "Can you sort an AdBlue / SCR warning?", a: "Yes — we diagnose NOx sensor faults, SCR system errors and refill or repair AdBlue systems on most modern diesels." },
    { q: "Do you handle van DPF and EGR work?", a: "Yes — Transits, Vivaros, Sprinters, Caddys, Berlingos and more. Class 7 MOT testing too if it's over 3 tonnes." },
  ],
  bookingContext: "diesel-dpf-egr",
  waPrefill: "Diesel warning light / DPF / EGR issue. Reg, make/model and warning shown: ",
  relatedLinks: [
    { to: "/diagnostics", label: "Diagnostics" },
    { to: "/mot", label: "MOT testing" },
    { to: "/mechanical-repairs", label: "Mechanical repairs" },
    { to: "/cost/diagnostics-east-london", label: "Diagnostics cost" },
    { to: "/guide/engine-light-east-london", label: "Engine light guide" },
    { to: "/garage-for-uber-bolt-drivers-east-london", label: "Uber / Bolt drivers" },
    { to: "/value-for-money-garage-east-london", label: "Value-for-money garage" },
    { to: "/google-maps-garage-e14", label: "Find us — sat-nav & yard entrance" },
  ],
};

export const Route = createFileRoute("/diesel-dpf-egr-east-london")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/diesel-dpf-egr-east-london",
      title: "Diesel DPF, EGR & Emissions Repair East London | Woodseer Autos",
      description:
        "Diesel DPF cleaning, EGR repair, AdBlue and emissions diagnostics in East London, E14. Honest quote first — no DPF removal. Woodseer Autos, Poplar.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Diesel DPF & EGR", path: "/diesel-dpf-egr-east-london" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        serviceSchema({
          name: "Diesel DPF, EGR & Emissions Repair",
          serviceType: "Diesel Diagnostics & Repair",
          description:
            "DPF cleaning and replacement, EGR valve repair, AdBlue/SCR diagnostics and emissions repair for diesel cars and vans in East London.",
          areaServed: "East London, E14",
          url: "https://woodseerautos.co.uk/diesel-dpf-egr-east-london",
        }),
        faqSchema(CONTENT.faqs) as Record<string, unknown>,
      ],
    }),
  component: () => <DriverLandingPage content={CONTENT} path="/diesel-dpf-egr-east-london" />,
});
